/**
 * Query tokenizer for full-text retrieval.
 *
 * Retrieval here is recall-oriented: a page is a candidate if it contains at
 * least one term from the question (terms OR'd, then ranked by BM25), because
 * callers pass whole natural-language questions and AND'ing every word matches
 * almost nothing. This module just splits a query into the term set the
 * browser-side index will OR together.
 */

/**
 * The term list for a query: whitespace-split, empties dropped. Empty or
 * whitespace-only input -> `[]`, which callers treat as "no results".
 */
export function queryTerms(query: string): string[] {
  return query.trim().split(/\s+/).filter(Boolean);
}

/**
 * Normalize a single token: lowercase, strip diacritics (NFKD + drop combining
 * marks), drop everything that is not a letter or number. Mirrors how the
 * desktop index tokenizes so search results line up.
 */
export function normalizeToken(token: string): string {
  return token
    .normalize('NFKD')
    .replace(/\p{M}+/gu, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, '');
}

/** Split arbitrary text into normalized index tokens (drops empties). */
export function tokenize(text: string): string[] {
  return text
    .split(/[^\p{L}\p{N}]+/u)
    .map(normalizeToken)
    .filter(Boolean);
}

/** Recall-oriented term set for a query, normalized like the index. */
export function normalizedQueryTerms(query: string): string[] {
  return queryTerms(query).map(normalizeToken).filter(Boolean);
}
